import { useState, type CSSProperties, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus } from 'lucide-react';
import { ChartEmpty, useEntryMotion } from '@/components/ui/charts';
import { avatarTone, deriveInitials } from '@/components/clients/team-avatars';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import type { CitationChipData } from '@/components/ui/citation-chip';
import {
  Callout,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
} from '@/components/ui/workspace';
import {
  useCreateInterviewNoteMutation,
  useInterviewNoteDetailQuery,
  useInterviewNoteListQuery,
  useProcessInterviewNoteMutation,
  type InterviewNoteListItem,
  type InterviewNoteTotals,
  type ProposedFinding,
} from '@/lib/api/interviews';
import { isProposalConfirmed, isProposalPending } from '@/lib/api/health-readiness-report';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { plural } from '../model';
import { FindingForm } from './finding-form';
import { humanize } from './pane-header';
import { ProposedFindingCard, extractFindingSummary } from './proposed-finding';

/**
 * Health & Readiness → Interview notes (T-0244; prototype
 * `interviewsTab()`): the summary (notes, findings proposed, how many a
 * person confirmed and how many wait) and one card per note with the
 * interviewee, an excerpt and its confirmed meter. Review opens the note
 * across the grid with the AI-proposed findings and their citations;
 * Confirm turns one into a register finding through the shared form (the
 * AI proposes, a person confirms; the API marks the proposal accepted in
 * the same write).
 *
 * One request draws the tab: the interviews list carries each note's
 * proposed / confirmed / pending counts and the engagement's totals, all
 * counted by the API. Only the note under review reads its detail.
 */

const NOTE_LIMIT = 200;
const EXCERPT_CHARS = 220;

function excerpt(body: string | null): string | null {
  if (!body) return null;
  const flat = body.replace(/\s+/g, ' ').trim();
  if (flat.length <= EXCERPT_CHARS) return flat;
  return `${flat.slice(0, EXCERPT_CHARS).trimEnd()}…`;
}

function proposedString(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = (value as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export function InterviewsPane({ engagementId }: { engagementId: string }) {
  const qc = useQueryClient();
  const list = useInterviewNoteListQuery(engagementId, { limit: NOTE_LIMIT });
  const notes = list.data?.items ?? [];
  const { animate } = useEntryMotion('interviews');

  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<ProposedFinding | null>(null);
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const startNew = () => {
    setNotice(null);
    setCreating((v) => !v);
  };
  const toggleReview = (id: string) => {
    setConfirming(null);
    setReviewingId((cur) => (cur === id ? null : id));
  };

  if (list.isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-body-sm text-ink-500"
        data-testid="interviews-pane"
      >
        <Spinner /> Loading interview notes…
      </div>
    );
  }
  if (list.error) {
    return (
      <div data-testid="interviews-pane">
        <ErrorMessage error={list.error} />
      </div>
    );
  }

  const form = creating ? (
    <NewNoteForm
      engagementId={engagementId}
      onCancel={() => setCreating(false)}
      onSaved={(processed) => {
        setCreating(false);
        setNotice(
          processed
            ? 'Note saved and sent for reading. Proposed findings appear on it when the AI is done.'
            : 'Note saved as a draft.',
        );
      }}
    />
  ) : null;

  if (notes.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="interviews-pane">
        {form ?? (
          <ChartEmpty title="No interview notes yet" className="py-14">
            Add a note after each stakeholder interview; the AI proposes findings from it and you
            confirm them.
            <div className="mt-3">
              <WorkspaceButton variant="primary" onClick={startNew}>
                <Plus className="size-3.5" aria-hidden="true" />
                New note
              </WorkspaceButton>
            </div>
          </ChartEmpty>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col gap-4', animate && 'chart-anim')}
      data-testid="interviews-pane"
    >
      <Summary totals={list.data?.totals ?? null} onNew={startNew} />
      {form}
      {notice ? (
        <Callout tone="ok" icon={CheckCircle2} role="status">
          {notice}
        </Callout>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {notes.map((note, i) => (
          <NoteCard
            key={note.id}
            note={note}
            index={i}
            reviewing={reviewingId === note.id}
            onReview={() => toggleReview(note.id)}
          >
            {reviewingId === note.id ? (
              <ReviewPanel
                engagementId={engagementId}
                note={note}
                confirming={confirming}
                onConfirm={(p) => {
                  setNotice(null);
                  setConfirming(p);
                }}
                onCancelConfirm={() => setConfirming(null)}
                onConfirmed={() => {
                  setConfirming(null);
                  setNotice('Finding confirmed. It is on the register now.');
                  void qc.invalidateQueries({
                    queryKey: ['interview-detail', engagementId, note.id],
                  });
                  void qc.invalidateQueries({ queryKey: ['interviews', engagementId] });
                }}
                onOpenCite={setOpenCite}
              />
            ) : null}
          </NoteCard>
        ))}
      </div>
      {list.data?.next_cursor ? (
        <p className="text-meta text-ink-500">Showing the latest {NOTE_LIMIT} notes.</p>
      ) : null}

      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open={openCite != null}
          onOpenChange={(open) => {
            if (!open) setOpenCite(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** The confirmed meter (prototype `.meter`): ink on the AI surface. */
function Meter({ done, of, i = 0 }: { done: number; of: number; i?: number }) {
  return (
    <span
      role="img"
      aria-label={`${done} of ${of} confirmed`}
      className="relative block h-2 flex-1 overflow-hidden rounded border border-ai-surface-border bg-ai-surface"
    >
      <i
        className="a-grow absolute inset-y-0 left-0 rounded-r-[3px] bg-ink-700"
        style={{ '--i': i, width: `${of > 0 ? (done / of) * 100 : 0}%` } as CSSProperties}
      />
    </span>
  );
}

/** The summary strip (prototype `.card.card-tight` atop the notes): the API's totals. */
function Summary({ totals, onNew }: { totals: InterviewNoteTotals | null; onNew: () => void }) {
  const label = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500';
  const big = 'text-[22px] font-semibold leading-[30px] text-ink-900';
  return (
    <section
      aria-label="Interview notes summary"
      className="flex flex-wrap items-center gap-6 rounded-card border border-ink-100 bg-paper-0 p-4"
    >
      <div>
        <span className={label}>Notes</span>
        <div className={big} data-testid="notes-total">
          {totals ? totals.notes : '—'}
        </div>
      </div>
      <div>
        <span className={label}>Findings proposed</span>
        <div className={big} data-testid="proposed-total">
          {totals ? totals.proposed : '—'}
        </div>
      </div>
      <div className="min-w-[240px] flex-1">
        <span className={label}>Confirmed by a person</span>
        {totals ? (
          <div className="mt-1.5 flex items-center gap-2.5">
            <Meter done={totals.confirmed} of={totals.proposed} />
            <span className="text-body-sm text-ink-700" data-testid="confirmed-line">
              <b className="font-semibold text-ink-900">{totals.confirmed}</b> of {totals.proposed}{' '}
              · {totals.pending} waiting
            </span>
          </div>
        ) : (
          <div className="mt-1.5 text-body-sm text-ink-500">—</div>
        )}
      </div>
      <WorkspaceButton variant="primary" onClick={onNew}>
        <Plus className="size-3.5" aria-hidden="true" />
        New note
      </WorkspaceButton>
    </section>
  );
}

/** Where a note with no proposals stands, in words. */
function readingState(note: InterviewNoteListItem): string {
  if (note.document_id === null) return 'Not sent for reading yet';
  switch (note.process_status) {
    case 'failed':
      return 'The AI could not read this note';
    case 'extracted':
      return 'No findings proposed';
    default:
      return 'The AI is reading this note';
  }
}

function NoteCard({
  note,
  index,
  reviewing,
  onReview,
  children,
}: {
  note: InterviewNoteListItem;
  index: number;
  reviewing: boolean;
  onReview: () => void;
  children: ReactNode;
}) {
  const who = note.interviewee_label?.trim() || note.title;
  const meta = [
    note.interviewee_label?.trim() ? note.title : null,
    formatDate(note.interview_date ?? note.created_at),
    note.created_by_name ?? null,
  ]
    .filter(Boolean)
    .join(' · ');
  const proposed = note.proposed_count ?? 0;
  const confirmed = note.confirmed_count ?? 0;
  const pending = note.pending_count ?? 0;
  const tone = avatarTone({ id: note.id, name: who });
  const quote = reviewing ? note.body_markdown?.trim() || null : excerpt(note.body_markdown);

  return (
    <article
      aria-labelledby={`note-${note.id}`}
      className={cn(
        'a-rise flex min-w-0 flex-col gap-3 rounded-card border bg-paper-0 p-4',
        reviewing ? 'border-ai-surface-border md:col-span-2' : 'border-ink-100',
      )}
      style={{ '--i': index } as CSSProperties}
      data-testid={`note-${note.id}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex size-8 flex-none items-center justify-center rounded-full border border-paper-0 text-[11px] font-semibold',
            `bg-avatar-${tone}-bg text-avatar-${tone}-ink`,
          )}
        >
          {deriveInitials(who.split(',')[0] ?? who)}
        </span>
        <div className="min-w-0">
          <h3
            id={`note-${note.id}`}
            className="truncate text-[15px] font-semibold leading-[22px] text-ink-900"
          >
            {who}
          </h3>
          <div className="truncate text-meta text-ink-500">{meta}</div>
        </div>
      </div>
      {quote ? (
        <p
          className={cn(
            'm-0 rounded-r-[6px] border-l-[3px] border-ink-200 bg-paper-50 px-3.5 py-2.5 text-[14px] leading-[22px] text-ink-900',
            reviewing && 'whitespace-pre-wrap',
          )}
        >
          {quote}
        </p>
      ) : null}
      <div className="flex items-center gap-2.5">
        {proposed > 0 ? (
          <>
            <Meter done={confirmed} of={proposed} i={index} />
            <span className="text-meta text-ink-500">
              {confirmed} of {plural(proposed, 'finding', 'findings')} confirmed
            </span>
          </>
        ) : (
          <span className="flex-1 text-meta text-ink-500">{readingState(note)}</span>
        )}
        {reviewing || pending > 0 ? (
          <WorkspaceButton variant="secondary" aria-expanded={reviewing} onClick={onReview}>
            {reviewing ? 'Close' : `Review ${pending}`}
          </WorkspaceButton>
        ) : null}
      </div>
      {children}
    </article>
  );
}

/** The note under review: its proposed findings, each confirmed through the shared form. */
function ReviewPanel({
  engagementId,
  note,
  confirming,
  onConfirm,
  onCancelConfirm,
  onConfirmed,
  onOpenCite,
}: {
  engagementId: string;
  note: InterviewNoteListItem;
  confirming: ProposedFinding | null;
  onConfirm: (p: ProposedFinding) => void;
  onCancelConfirm: () => void;
  onConfirmed: () => void;
  onOpenCite: (cite: CitationChipData) => void;
}) {
  const detail = useInterviewNoteDetailQuery(engagementId, note.id);
  if (detail.isLoading) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading proposed findings…
      </div>
    );
  }
  if (detail.error) return <ErrorMessage error={detail.error} />;
  const proposals = detail.data?.proposed_findings ?? [];
  return (
    <section
      aria-label="Proposed findings"
      className="flex flex-col gap-2 border-t border-ink-100 pt-3"
    >
      <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
        Proposed findings · {proposals.length}
      </h4>
      {proposals.length === 0 ? (
        <p className="text-meta text-ink-500">No findings proposed from this note.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {proposals.map((p) => (
            <ProposedFindingCard
              key={p.proposal_id}
              finding={p}
              onOpenCite={onOpenCite}
              action={
                isProposalPending(p.status) ? (
                  <WorkspaceButton variant="secondary" onClick={() => onConfirm(p)}>
                    Confirm
                  </WorkspaceButton>
                ) : isProposalConfirmed(p.status) ? (
                  <StatusChip tone="healthy" size="sm">
                    Confirmed
                  </StatusChip>
                ) : (
                  <StatusChip tone="neutral" size="sm">
                    {humanize(p.status)}
                  </StatusChip>
                )
              }
            />
          ))}
        </ul>
      )}
      {confirming ? (
        <FindingForm
          key={confirming.proposal_id}
          engagementId={engagementId}
          heading="Confirm the proposed finding"
          submitLabel="Confirm finding"
          initial={{
            title: extractFindingSummary(confirming.proposed_value) ?? '',
            type:
              proposedString(confirming.proposed_value, 'type') ??
              proposedString(confirming.proposed_value, 'category'),
            severity: proposedString(confirming.proposed_value, 'severity'),
            description: proposedString(confirming.proposed_value, 'description'),
          }}
          fromProposal={{ proposalId: confirming.proposal_id, documentId: note.document_id }}
          onCancel={onCancelConfirm}
          onSaved={onConfirmed}
        />
      ) : null}
    </section>
  );
}

/**
 * A typed note. "Save and read with AI" sends it through the document
 * pipeline (the existing process endpoint), which is what produces the
 * proposed findings.
 */
function NewNoteForm({
  engagementId,
  onCancel,
  onSaved,
}: {
  engagementId: string;
  onCancel: () => void;
  onSaved: (processed: boolean) => void;
}) {
  const create = useCreateInterviewNoteMutation(engagementId);
  const process = useProcessInterviewNoteMutation(engagementId);
  const [title, setTitle] = useState('');
  const [who, setWho] = useState('');
  const [date, setDate] = useState('');
  const [body, setBody] = useState('');
  const busy = create.isPending || process.isPending;
  const canSubmit = title.trim().length > 0 && !busy;

  function save(readWithAi: boolean) {
    if (!canSubmit) return;
    create.mutate(
      {
        title: title.trim(),
        interviewee_label: who.trim() || null,
        interview_date: date || null,
        body_markdown: body.trim() || null,
      },
      {
        onSuccess: (res) => {
          if (readWithAi && res.id) {
            process.mutate({ interviewNoteId: res.id }, { onSuccess: () => onSaved(true) });
          } else {
            onSaved(false);
          }
        },
      },
    );
  }

  return (
    <section
      aria-label="New interview note"
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <h3 className="t-h3">New interview note</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <WorkspaceField label="Title" className="sm:col-span-3">
          {(id) => (
            <WorkspaceInput
              id={id}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. IT support manager — hypercare staffing"
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Interviewee" className="sm:col-span-2">
          {(id) => <WorkspaceInput id={id} value={who} onChange={(e) => setWho(e.target.value)} />}
        </WorkspaceField>
        <WorkspaceField label="Date">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Notes" className="sm:col-span-3">
          {(id) => (
            <textarea
              id={id}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="w-full rounded-card border border-ink-200 bg-paper-0 p-2 text-body-sm text-ink-900 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
              placeholder="What was said, risks raised, follow-ups"
            />
          )}
        </WorkspaceField>
      </div>
      {create.error ? <ErrorMessage error={create.error} className="mt-2" /> : null}
      {process.error ? <ErrorMessage error={process.error} className="mt-2" /> : null}
      <div className="mt-3 flex justify-end gap-2">
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
        <WorkspaceButton disabled={!canSubmit} onClick={() => save(false)}>
          Save draft
        </WorkspaceButton>
        <WorkspaceButton variant="primary" disabled={!canSubmit} onClick={() => save(true)}>
          {busy ? <Spinner /> : null}
          Save and read with AI
        </WorkspaceButton>
      </div>
    </section>
  );
}
